/**
 * Utility functions for handling API errors consistently across the application.
 */

/**
 * Extract a user-friendly error message from an Axios error response.
 * Handles FastAPI/Pydantic validation errors and custom error responses.
 *
 * @param error - The error object from a failed API call
 * @param defaultMessage - Default message to use if error cannot be parsed
 * @returns A formatted error message string
 */
export const formatApiError = (error: any, defaultMessage: string = 'An error occurred'): string => {
    // Check if there's a response with detail
    if (error.response?.data?.detail) {
        const detail = error.response.data.detail;

        // String error message
        if (typeof detail === 'string') {
            return detail;
        }

        // Pydantic validation errors (array of error objects)
        if (Array.isArray(detail)) {
            return detail.map((err: any) => {
                const location = err.loc?.join('.') || 'unknown';
                return `${location}: ${err.msg}`;
            }).join(', ');
        }

        // Object error - stringify it
        if (typeof detail === 'object') {
            return JSON.stringify(detail);
        }
    }

    // Check for generic error message
    if (error.response?.data?.message) {
        return error.response.data.message;
    }

    // Check for error message directly
    if (error.message) {
        return error.message;
    }

    return defaultMessage;
};

